import { createContext, useContext, useState, useMemo, useEffect } from "react";
import axios from "axios";

const CartContext = createContext(null);

const STORAGE_KEY = "gh_cart_v1";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [tip, setTip] = useState(0);
  const [compCode, setCompCodeRaw] = useState("");
  const [feeWaived, setFeeWaived] = useState(false);
  const [surchargeAmount, setSurchargeAmount] = useState(1.0);
  const [orderLimit, setOrderLimit] = useState(20.0);

  const setCompCode = (code) => {
    setCompCodeRaw(code);
    // Any edit invalidates prior waiver — user must re-validate via checkout.
    if (feeWaived) setFeeWaived(false);
  };

  // Pull the live surcharge from the server so it matches whatever the owner
  // last set. Refresh occasionally so long-lived tabs stay in sync.
  useEffect(() => {
    let cancelled = false;
    const fetchSettings = () => {
      axios
        .get(`${API}/settings`)
        .then((r) => {
          if (cancelled) return;
          if (typeof r.data?.surcharge === "number") {
            setSurchargeAmount(r.data.surcharge);
          }
          if (typeof r.data?.order_subtotal_limit === "number") {
            setOrderLimit(r.data.order_subtotal_limit);
          }
        })
        .catch(() => {});
    };
    fetchSettings();
    const t = setInterval(fetchSettings, 60000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (item) => {
    const cartKey = item.cart_key || item.id;
    setItems((prev) => {
      const existing = prev.find((i) => (i.cart_key || i.id) === cartKey);
      if (existing) {
        return prev.map((i) =>
          (i.cart_key || i.id) === cartKey
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }
      return [...prev, { ...item, cart_key: cartKey, quantity: 1 }];
    });
  };

  const updateQuantity = (cartKey, quantity) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((i) => (i.cart_key || i.id) !== cartKey));
    } else {
      setItems((prev) =>
        prev.map((i) =>
          (i.cart_key || i.id) === cartKey ? { ...i, quantity } : i
        )
      );
    }
  };

  const removeItem = (cartKey) =>
    setItems((prev) => prev.filter((i) => (i.cart_key || i.id) !== cartKey));

  const clearCart = () => {
    setItems([]);
    setTip(0);
    setCompCodeRaw("");
    setFeeWaived(false);
  };

  const { count, subtotal, tax, surcharge, baseSurcharge, total, overLimit } = useMemo(() => {
    const count = items.reduce((a, i) => a + i.quantity, 0);
    const subtotal = items.reduce((a, i) => a + i.price * i.quantity, 0);
    const tax = 0;
    const baseSurcharge = subtotal > 0 ? Number(surchargeAmount) || 0 : 0.0;
    const surcharge = feeWaived ? 0 : baseSurcharge;
    const safeTip = Number.isFinite(tip) && tip > 0 ? tip : 0;
    const total = Math.round((subtotal + surcharge + safeTip) * 100) / 100;
    const overLimit = subtotal > (Number(orderLimit) || 20);
    return {
      count,
      subtotal: Math.round(subtotal * 100) / 100,
      tax,
      surcharge,
      baseSurcharge,
      total,
      overLimit,
    };
  }, [items, tip, feeWaived, surchargeAmount, orderLimit]);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        updateQuantity,
        removeItem,
        clearCart,
        count,
        subtotal,
        tax,
        surcharge,
        baseSurcharge,
        tip,
        setTip,
        total,
        overLimit,
        compCode,
        setCompCode,
        feeWaived,
        setFeeWaived,
        ORDER_LIMIT: Number(orderLimit) || 20,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
